import type { IOrdersEdgesNode } from "../types/api/customer";
import type { IReturnLineItem, IReturnRequestedItem, IReturnableFulfillmentLineItem } from "../types/api/return";
import type { IAddresses } from "../types/domain/address";
import { ReturnReason } from "../types/enum";
import type { Metaobject } from "../types/generated/storefront.types";
import { WeightUnit } from "../types/generated/storefront.types";

const weightConversion: Record<WeightUnit, number> = {
  [WeightUnit.Grams]: 1000,
  [WeightUnit.Kilograms]: 1,
  [WeightUnit.Ounces]: 35.274,
  [WeightUnit.Pounds]: 2.205,
};

const DEFAULT_SHIPPING_FEE = 6000;

const UNIT_TO_KG = {
  [WeightUnit.Grams]: 1000,
  [WeightUnit.Kilograms]: 1,
  [WeightUnit.Ounces]: 35.274,
  [WeightUnit.Pounds]: 2.205,
};

const getShippingInfo = ({
  order,
  weightSum,
  country,
  lang,
  addresses,
  defaultWeight,
}: {
  order?: IOrdersEdgesNode | null;
  weightSum?: number;
  country?: string;
  lang: "ko" | "en" | "jp";
  addresses: IAddresses;
  defaultWeight: number;
}) => {
  if (lang === "ko") {
    return { shippingMethod: "CJ 대한통운", shippingFee: 0 };
  }

  const shippingCountry = country ?? (order?.shippingAddress?.country || "");

  const calcWeight =
    order?.lineItems.edges.reduce((sum, { node }) => {
      const unit = node.variant?.weightUnit || WeightUnit.Kilograms;
      const calcUnit = UNIT_TO_KG[unit];
      return sum + (parseFloat(String(node.variant?.weight ?? 1)) / calcUnit) * node.quantity;
    }, 0) || 0;

  const orderWeight = Math.ceil((calcWeight + defaultWeight) * 10) / 10;

  const totalWeight = weightSum ?? orderWeight;

  // 배송지 국가에 따른 배송 정보 가져오기
  const locationGroupZone = addresses.data.locationGroupZones.edges.find(({ zone }) =>
    zone.countries.some(({ name }) => name === shippingCountry),
  );

  // 가장 비용이 큰 zone
  const biggestRate = locationGroupZone?.rateDefinitions.edges[locationGroupZone.rateDefinitions.edges.length - 1];

  const maxCondition = biggestRate?.node.methodConditions.find((item) => item.operator.includes("GREATER"));

  // 배송 무게가 최대치인지
  const isOverMaxWeight = (maxCondition?.conditionCriteria.value ?? 0) < totalWeight;

  // 배송 무게에 맞는 구간
  const matchedZone = locationGroupZone?.rateDefinitions.edges.find(({ node }) => {
    const min = node.methodConditions.find((item) => item.operator.includes("GREATER"))?.conditionCriteria.value ?? 0;
    const max = node.methodConditions.find((item) => item.operator.includes("LESS"))?.conditionCriteria.value ?? 0;
    return min <= totalWeight && totalWeight <= max;
  });

  // 배송 구간 찾기
  const shippingInfo = isOverMaxWeight ? biggestRate : matchedZone;

  const shippingZone = locationGroupZone?.zone.countries.find((item) => item.name === shippingCountry);

  const shippingTitle = order?.customAttributes.find((item) => item.key === "shippingTitle");

  const shippingMethod = shippingTitle ? shippingTitle.value : shippingInfo?.node.name || "Shipping";
  const shippingFee = parseFloat(shippingInfo?.node.rateProvider.price.amount || "0");

  return { shippingZone, shippingMethod, shippingFee };
};

/**
 * @description 반품 가능한 상품
 */
export const getReturnableItemData = (returnableItem: IReturnableFulfillmentLineItem | IReturnLineItem) => {
  const originalPrice = parseFloat(returnableItem.fulfillmentLineItem.originalTotalSet.shopMoney.amount || "0");

  const calculatedDiscountAmount = returnableItem.fulfillmentLineItem.lineItem.discountAllocations
    .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount || "0"))
    .reduce((a, b) => a + b, 0);

  const totalDiscountedPrice = originalPrice - calculatedDiscountAmount;

  const size = returnableItem.fulfillmentLineItem.lineItem.variant
    ? returnableItem.fulfillmentLineItem.lineItem.variant.selectedOptions.find((option) => option.name === "Size")?.value || ""
    : (/\/\s*(?<number>\d+)$/.exec(returnableItem.fulfillmentLineItem.lineItem.variantTitle || "") || [])[1];

  const color = returnableItem.fulfillmentLineItem.lineItem.variant
    ? returnableItem.fulfillmentLineItem.lineItem.variant.selectedOptions.find((option) => option.name === "Color")?.value || ""
    : (/\/\s*(?<number>\d+)$/.exec(returnableItem.fulfillmentLineItem.lineItem.variantTitle || "") || [])[1];

  return { originalPrice, calculatedDiscountAmount, totalDiscountedPrice, size, color };
};

export const calculateReturnShippingFee = ({
  shippingFee,
  isFreeReturnShipping,
  totalOrderItemCount,
  totalReturnedItemCount,
  totalReturnRequestItemCount,
}: {
  shippingFee: number;
  isFreeReturnShipping: boolean;
  totalOrderItemCount: number;
  totalReturnedItemCount: number;
  totalReturnRequestItemCount: number;
}) => {
  // DEFECTIVE 사유로 반품이 포함된 경우
  if (isFreeReturnShipping) {
    return 0;
  }

  // 모든 상품을 반품한 경우
  if (totalOrderItemCount === totalReturnRequestItemCount) {
    return shippingFee;
  }

  // 반품 신청하려는 상품이 마지막 남은 상품일 경우
  if (totalReturnRequestItemCount === totalOrderItemCount - totalReturnedItemCount) {
    return shippingFee;
  }

  // 일부 상품을 반품하는 경우
  if (totalReturnRequestItemCount <= totalOrderItemCount - totalReturnedItemCount) {
    return shippingFee / 2;
  }

  // return 0;
  return shippingFee;
};

/** KR 반품 배송비를 계산하는 함수 */
export const getShippingFeeKr = ({
  isFreeReturnShipping,
  totalOrderItemCount,
  totalReturnedItemCount,
  totalReturnRequestItemCount,
  shippingFee,
}: {
  isFreeReturnShipping: boolean;
  totalOrderItemCount: number;
  totalReturnedItemCount: number;
  totalReturnRequestItemCount: number;
  shippingFee?: number;
}): number => {
  return calculateReturnShippingFee({
    shippingFee: shippingFee ?? DEFAULT_SHIPPING_FEE,
    isFreeReturnShipping,
    totalOrderItemCount,
    totalReturnedItemCount,
    totalReturnRequestItemCount,
  });
};

/** INTL 반품 배송비를 계산하는 함수 */
export const getShippingFeeIntl = ({
  checkedLineItems,
  country,
  isFreeReturnShipping,
  totalOrderItemCount,
  totalReturnedItemCount,
  totalReturnRequestItemCount,
  addresses,
  defaultBoxWeight = 3,
}: {
  checkedLineItems: IReturnRequestedItem[];
  country: string;
  isFreeReturnShipping: boolean;
  totalOrderItemCount: number;
  totalReturnedItemCount: number;
  totalReturnRequestItemCount: number;
  addresses: IAddresses;
  /** 기본 박스 무게(kg). 스토어별로 상이할 수 있어 파라미터로 받음 (기본값 3kg) */
  defaultBoxWeight?: number;
}) => {
  const wightSum = checkedLineItems
    .map((item) => {
      const calcWeight = weightConversion[item.weight?.unit as WeightUnit] || 1;
      return (Number(item.weight?.value) / calcWeight) * item.quantity;
    })
    .reduce((a, b) => a + b, 0);

  // 모든 배송비 무게에 기본 박스 무게 추가되어야함
  const totalWeight = Math.ceil((wightSum + defaultBoxWeight) * 10) / 10;

  const { shippingFee } = getShippingInfo({
    country,
    weightSum: totalWeight,
    addresses,
    defaultWeight: defaultBoxWeight,
    lang: "en",
  });

  return calculateReturnShippingFee({
    shippingFee,
    isFreeReturnShipping,
    totalOrderItemCount,
    totalReturnedItemCount,
    totalReturnRequestItemCount,
  });
};

/** 사은품 반품 처리 함수 */
// 남아있는 상품(체크하지 않은 상품)을 기준으로 사은품의 반품 여부를 판단
export const processGiftReturnItems = ({
  returnableGiftItems,
  remainingLineItems,
  giftByProduct,
  giftByPrice,
}: {
  returnableGiftItems: IReturnableFulfillmentLineItem[];
  remainingLineItems: IReturnableFulfillmentLineItem[];
  giftByPrice: Metaobject[] | null;
  giftByProduct: Metaobject[] | null;
}) => {
  // Get gift metaObject by product ID
  const getGiftMetaObject = (giftList: Metaobject[] | null, productId: string) =>
    giftList?.find((gift) => gift.fields.some((field) => field.key === "gift_product" && field.value?.includes(productId)));

  const processedGiftReturnItems = returnableGiftItems
    .filter((returnableGiftItem) => {
      /**
       * 남아있는 상품이 없으면 모든 gift 자동 체크
       * gift metaobjects 가 draft 상태일 때는 사은품 조건을 확인할 수 없어서 자동 체크가 되지 않음.
       * 이 경우를 방지하기 위해 남아있는 상품이 없으면 무조건 isCheck = true 처리
       */
      if (remainingLineItems.length === 0) return true;

      const { product } = returnableGiftItem.fulfillmentLineItem.lineItem;

      // 1. Check giftByProduct Condition
      const giftByProductObject = getGiftMetaObject(giftByProduct, product.id);
      if (giftByProductObject) {
        // 지급 기준 충족 여부 - 남아있는 상품이 사은품의 condition_product에 포함되는지 확인
        const hasConditionProduct = remainingLineItems.some((item) =>
          giftByProductObject.fields.some(
            (field) => field.key === "condition_product" && field.value?.includes(item.fulfillmentLineItem.lineItem.product.id),
          ),
        );
        if (hasConditionProduct) return false;
      }

      // 2. Check giftByPrice Condition
      const giftByPriceObject = getGiftMetaObject(giftByPrice, product.id);
      if (giftByPriceObject) {
        // 지급 기준 충족 여부 - 남아있는 상품 합계 금액이 지급 조건 금액을 넘는지 확인
        const priceCondition = parseFloat(giftByPriceObject.fields.find((field) => field.key === "price_condition")?.value ?? "0");
        const totalDiscountedPrice = remainingLineItems.reduce((total, item) => {
          const originalPrice = parseFloat(item.fulfillmentLineItem.originalTotalSet.shopMoney.amount);
          const totalDiscountAmount = item.fulfillmentLineItem.lineItem.discountAllocations.reduce(
            (sum, { allocatedAmount }) => sum + parseFloat(allocatedAmount.amount ? allocatedAmount.amount : "0"),
            0,
          );
          const unitAmount = (originalPrice - totalDiscountAmount) / item.fulfillmentLineItem.quantity;
          return total + unitAmount * item.quantity;
        }, 0);

        if (totalDiscountedPrice >= priceCondition) return false;
      }

      return giftByProductObject || giftByPriceObject;
    })
    .map((item) => {
      const originalTotalSet = parseFloat(item.fulfillmentLineItem.originalTotalSet.shopMoney.amount);
      const calculatedDiscountAmount = item.fulfillmentLineItem.lineItem.discountAllocations
        .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount))
        .reduce((a, b) => a + b, 0);

      const discountedTotalSet = originalTotalSet - calculatedDiscountAmount;

      return {
        id: item.fulfillmentLineItem.id,
        quantity: item.fulfillmentLineItem.quantity,
        isCheck: true,
        image: null,
        returnReason: ReturnReason.OTHER,
        customerNote: "",
        cost: discountedTotalSet,
      };
    });

  return processedGiftReturnItems;
};
